import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { http } from '../../lib/http';
import { useAuth } from '../../contexts/AuthContext';

const TwoFactorVerification = () => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  
  const navigate = useNavigate();
  const location = useLocation();
  const { complete2FA } = useAuth();
  const userEmail = location.state?.email || '';
  const userName = location.state?.name || 'Owner';

  useEffect(() => {
    // Redirect if no email provided
    if (!userEmail) {
      navigate('/login');
    }
  }, [userEmail, navigate]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    
    if (code.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);

    try {
      const response = await http.post('/auth/verify-2fa', {
        email: userEmail,
        code: code
      });

      if (response.data.access_token) {
        const { access_token, user: userData } = response.data;
        
        // Complete 2FA authentication using AuthContext
        complete2FA(access_token, userData);
        
        // Small delay to ensure state is updated before navigation
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 100);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code. Please try again.');
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResendMessage('');
    setError('');

    try {
      const response = await http.post('/auth/resend-2fa', {
        email: userEmail
      });

      if (response.data.success) {
        setResendMessage('New code sent to your email!');
        setTimeout(() => setResendMessage(''), 5000);
      }
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 6) {
      setCode(value);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border-4 border-orange-500">
          {/* Header */}
          <div className="bg-gradient-to-r from-black to-gray-900 p-8 text-center">
            <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Just Protein</h1>
            <p className="text-orange-500 text-sm font-semibold">Gym Meals Provider</p>
          </div>

          {/* Content */}
          <div className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">Verify Your Identity</h2>
            <p className="text-gray-600 text-center mb-6">
              Hello <span className="font-semibold text-gray-900">{userName}</span>! We've sent a 6-digit code to <span className="font-semibold text-gray-900">{userEmail}</span>
            </p>

            <form onSubmit={handleVerify} className="space-y-6">
              {/* Code Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-center">
                  Enter Verification Code
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={handleCodeChange}
                  placeholder="000000"
                  maxLength={6}
                  className="w-full px-6 py-4 text-center text-3xl font-bold tracking-widest border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                  disabled={loading}
                  autoFocus
                />
                <p className="text-xs text-gray-500 text-center mt-2">Code expires in 10 minutes</p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-3">
                  <p className="text-red-600 text-sm text-center">{error}</p>
                </div>
              )}

              {/* Success Message */}
              {resendMessage && (
                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-3">
                  <p className="text-green-600 text-sm text-center">{resendMessage}</p>
                </div>
              )}

              {/* Verify Button */}
              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:from-gray-300 disabled:to-gray-400 text-white font-bold py-4 rounded-lg transition-all duration-200 shadow-lg disabled:cursor-not-allowed"
              >
                {loading ? 'Verifying...' : 'Verify & Login'}
              </button>
            </form>

            {/* Resend Link */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600 mb-2">Didn't receive the code?</p>
              <button
                onClick={handleResend}
                disabled={resending}
                className="text-orange-500 hover:text-orange-600 font-semibold text-sm disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {resending ? 'Sending...' : 'Resend Code'}
              </button>
            </div>

            {/* Back to Login */}
            <div className="mt-4 text-center">
              <button
                onClick={() => navigate('/login')}
                className="text-gray-500 hover:text-gray-700 text-sm transition-colors"
              >
                ← Back to Login
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-500 text-xs mt-6">
          © 2025 Just Protein. Secure login with two-factor authentication.
        </p>
      </div>
    </div>
  );
};

export default TwoFactorVerification;
